import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";

// Constants
export const TREDOT_BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
export const API_PREFIX = "/api/v1";
export const REQUEST_TIMEOUT = 80000;

// Types
export interface TokenPayload {
  id: string | number;
  email: string;
  role: string | undefined;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  accessToken?: string;
  refreshToken?: string;
}

export interface AuthResponse {
  user: User;
  token?: string;
  message?: string;
}

export interface APIError {
  errors?: { path: string; msg: string }[];
  error?: string;
  message?: string;
}

// Storage abstraction layer
class StorageManager {
  private memoryStorage: Map<string, string> = new Map();
  private storageAvailable: boolean = false;

  constructor() {
    this.checkStorageAvailability();
  }

  private checkStorageAvailability(): void {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      this.storageAvailable = true;
    } catch (e) {
      console.warn('localStorage not available, using memory storage');
      this.storageAvailable = false;
    }
  }

  setItem(key: string, value: string): void {
    this.memoryStorage.set(key, value);
    
    if (this.storageAvailable) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn('Failed to write to localStorage:', e);
        this.storageAvailable = false;
      }
    }
  }

  getItem(key: string): string | null {
    if (this.storageAvailable) {
      try {
        const value = localStorage.getItem(key);
        if (value) {
          this.memoryStorage.set(key, value);
          return value;
        }
      } catch (e) {
        console.warn('Failed to read from localStorage:', e);
        this.storageAvailable = false;
      }
    }
    
    return this.memoryStorage.get(key) || null;
  }

  removeItem(key: string): void {
    this.memoryStorage.delete(key);
    
    if (this.storageAvailable) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('Failed to remove from localStorage:', e);
      }
    }
  }

  clear(): void {
    this.memoryStorage.clear();
    
    if (this.storageAvailable) {
      try {
        localStorage.clear();
      } catch (e) {
        console.warn('Failed to clear localStorage:', e);
      }
    }
  }
}

// AuthService Class
export class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: User | null = null; // ✅ NEW: Store user data
  private readonly AUTH_STORAGE_KEY = "tredot_auth_data";
  private apiClient: AxiosInstance;
  private storage: StorageManager;
  private refreshPromise: Promise<string | null> | null = null;
  private sessionCheckPromise: Promise<boolean> | null = null;
  constructor() {
    this.storage = new StorageManager();
    
    this.apiClient = axios.create({
      baseURL: `${TREDOT_BACKEND_URL}${API_PREFIX}`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
      timeout: REQUEST_TIMEOUT,
    });

    this.initializeTokens();
  }

  private initializeTokens(): void {
    const authData = this.storage.getItem(this.AUTH_STORAGE_KEY);
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        // Handle both key formats for backward compatibility
        this.accessToken = parsed.accessToken || parsed.tredot_accessToken;
        this.refreshToken = parsed.refreshToken || parsed.tredot_refreshToken;
        this.user = parsed.user; // ✅ NEW: Load user data
      } catch (e) {
        console.warn('Failed to parse stored auth data');
      }
    }
  }

  getUser(): User | null {
    return this.user;
  }

  // ✅ NEW: Get user role
  getUserRole(): string | null {
    return this.user?.role || null;
  }

  // ✅ NEW: Get dashboard route based on role
  getDashboardRoute(): string {
    const role = this.getUserRole();
    
    switch (role?.toLowerCase()) {
      case 'student':
        return '/studentdashboard';
      case 'tutor':
      case 'teacher':
        return '/teachersdashboard';
      case 'organization':
        return '/organization/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/'; // Fallback
    }
  }


  async getAccessToken(): Promise<string | null> {
    if (this.accessToken) {
      return this.accessToken;
    }

    this.accessToken = await this.retrieveAccessToken();
    return this.accessToken;
  }

  async getRefreshToken(): Promise<string | null> {
    if (this.refreshToken) {
      return this.refreshToken;
    }
    this.refreshToken = await this.retrieveRefreshToken();
    return this.refreshToken;
  }

  async setTokens(tokens: Tokens, user?: User): Promise<void> {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;
    
    if (user) {
      this.user = user; // ✅ NEW: Store user data
    }

    const authData = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user: this.user, // ✅ NEW: Include user in storage
    };
    
    this.storage.setItem(this.AUTH_STORAGE_KEY, JSON.stringify(authData));
  }

  // ✅ NEW: Check if valid session exists (using cookies)
  async checkSession(): Promise<boolean> {
    // Prevent multiple simultaneous session checks
    if (this.sessionCheckPromise) {
      return this.sessionCheckPromise;
    }

    this.sessionCheckPromise = this._checkSession();
    const result = await this.sessionCheckPromise;
    this.sessionCheckPromise = null;
    return result;
  }

  private async _checkSession(): Promise<boolean> {
    try {
      const response = await this.apiClient.get("/auth/session");
      
      if (response.data.authenticated && response.data.token) {
        // Sync token and user from cookie to memory
        await this.setTokens(
          {
            accessToken: response.data.token,
            refreshToken: this.refreshToken || "",
          },
          response.data.user // ✅ NEW: Store user data from session
        );
        
        console.log("Session validated from cookies");
        return true;
      }
      
      return false;
    } catch (error) {
      console.log("No valid session found");
      return false;
    }
  }
  async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._refreshAccessToken();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  private async _refreshAccessToken(): Promise<string | null> {
    try {
      const response = await this.apiClient.post(
        "/auth/refresh-token",
        {},
        {
          headers: {
            "X-Retry": "true",
          },
          timeout: REQUEST_TIMEOUT,
        }
      );

      if (response.data && response.data.token) {
        const newToken = response.data.token;
        
        await this.setTokens(
          {
            accessToken: newToken,
            refreshToken: (await this.getRefreshToken()) || "",
          },
          response.data.user // ✅ NEW: Update user data if provided
        );

        toast.success("Session refreshed successfully", {
          duration: 2000,
        });

        return newToken;
      }
      return null;
    } catch (error) {
      console.error("Error refreshing token:", error);
      
      if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
        toast.error(
          "Request timed out. Server may be sleeping. Please try again.",
          {
            duration: 6000,
            action: {
              label: "Retry",
              onClick: () => window.location.reload(),
            },
          }
        );
      } else {
        toast.error("Session expired. Please login again.", {
          duration: 4000,
          action: {
            label: "Login",
            onClick: () => (window.location.href = "/login"),
          },
        });
      }
      
      await this.logout();
      return null;
    }
  }

  async logout(): Promise<void> {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null; // ✅ NEW: Clear user data
    this.storage.removeItem(this.AUTH_STORAGE_KEY);

    try {
      await this.apiClient.post("/auth/logout", {});
    } catch (e) {
      console.warn('Logout endpoint failed:', e);
    }

    toast.info("You have been logged out", {
      duration: 3000,
    });
  }

  isAuthenticated(): boolean {
    return this.accessToken !== null || this.storage.getItem(this.AUTH_STORAGE_KEY) !== null;
  }


  private async retrieveAccessToken(): Promise<string | null> {
    const authData = this.storage.getItem(this.AUTH_STORAGE_KEY);
    if (!authData) return null;
    
    try {
      const parsed = JSON.parse(authData);
      return parsed.accessToken || parsed.tredot_accessToken;
    } catch {
      return null;
    }
  }

  private async retrieveRefreshToken(): Promise<string | null> {
    const authData = this.storage.getItem(this.AUTH_STORAGE_KEY);
    if (!authData) return null;
    
    try {
      const parsed = JSON.parse(authData);
      return parsed.refreshToken || parsed.tredot_refreshToken;
    } catch {
      return null;
    }
  }

  getAuthStatus(): { 
    isAuthenticated: boolean; 
    dashboardRoute: string;
    user: User | null;
  } {
    return {
      isAuthenticated: this.isAuthenticated(),
      dashboardRoute: this.getDashboardRoute(),
      user: this.getUser(),
    };
  }
}

// ApiService Class
class ApiService {
  public apiClient: AxiosInstance;
  public authService: AuthService;
  private sessionInitialized: boolean = false;

  constructor() {
    this.authService = new AuthService();

    this.apiClient = axios.create({
      baseURL: `${TREDOT_BACKEND_URL}${API_PREFIX}`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
      timeout: REQUEST_TIMEOUT,
    });

    this.initializeRequestInterceptor();
    this.initializeResponseInterceptor();
  }

  // ✅ NEW: Initialize session on app load
  async initializeSession(): Promise<boolean> {
    if (this.sessionInitialized) {
      return this.authService.isAuthenticated();
    }

    // Check if we have tokens in memory/storage
    const hasLocalToken = await this.authService.getAccessToken();
    
    if (hasLocalToken) {
      this.sessionInitialized = true;
      return true;
    }

    // No local token, check if valid session exists in cookies
    const hasValidSession = await this.authService.checkSession();
    this.sessionInitialized = true;
    
    return hasValidSession;
  }

  private initializeRequestInterceptor() {
    this.apiClient.interceptors.request.use(
      this.requestInterceptor,
      (error) => Promise.reject(error)
    );
  }

  private requestInterceptor = async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    const token = await this.authService.getAccessToken();
    
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    
    if (!config.timeout) {
      config.timeout = REQUEST_TIMEOUT;
    }
    
    return config;
  };

  private initializeResponseInterceptor() {
    this.apiClient.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Only attempt token refresh on 401 (Unauthorized / token expired).
        // 403 (Forbidden) means the server recognised the user but denied access —
        // refreshing the token won't help and should never trigger a logout.
        if (
          error.response?.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          const newAccessToken = await this.authService.refreshAccessToken();

          if (newAccessToken) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            originalRequest.timeout = REQUEST_TIMEOUT;
            return this.apiClient(originalRequest);
          } else {
            await this.authService.logout();
            window.location.href = "/login";
            return Promise.reject(error);
          }
        }

        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }

  public handleApiError(error: any): string[] {
    const errorMessages: string[] = [];
  
    if (error.response?.data) {
      const apiError: APIError = error.response.data;
  
      if (apiError.errors && Array.isArray(apiError.errors)) {
        const validationErrors = apiError.errors.map(
          (e) => `${e.path}: ${e.msg}`
        );
  
        toast.error("Validation Error", {
          description: validationErrors.join(", "),
          id: validationErrors.join(","),
          duration: 5000,
          action: {
            label: "Dismiss",
            onClick: () => {},
          },
        });
  
        errorMessages.push(...validationErrors);
      } else if (apiError.message) {
        toast.error(apiError.error || "Error", {
          id: apiError.message,
          description: apiError.message,
          duration: 4000,
        });
        errorMessages.push(apiError.message);
      } else if (apiError.error) {
        toast.error("Request Failed", {
          id: apiError.error,
          description: typeof apiError.error === 'string' ? apiError.error : "An error occurred",
          duration: 4000,
        });
        errorMessages.push(typeof apiError.error === 'string' ? apiError.error : "An error occurred");
      }
    } else if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
      toast.error("Request Timeout", {
        id: "request-timeout",
        description: "The server is taking longer than expected. Please try again.",
        duration: 6000,
        action: {
          label: "Retry",
          onClick: () => window.location.reload(),
        },
      });
      errorMessages.push("Request timeout");
    } else if (
      error.code === "NETWORK_ERROR" ||
      error.message?.includes("Network Error")
    ) {
      toast.error("Network Error", {
        id: "network-error",
        description: "Please check your internet connection.",
        duration: 5000,
        action: {
          label: "Retry",
          onClick: () => window.location.reload(),
        },
      });
      errorMessages.push("Network connection error");
    } else {
      const message = error.message || "An unexpected error occurred";
      toast.error("Something went wrong", {
        id: "unexpected-error",
        description: message,
        duration: 4000,
      });
      errorMessages.push(message);
    }
  
    return errorMessages;
  }

  public showSuccessToast(message: string, description?: string): void {
    toast.success(message, { description, duration: 3000 });
  }

  public showInfoToast(message: string, description?: string): void {
    toast.info(message, { description, duration: 3000 });
  }

  public showWarningToast(message: string, description?: string): void {
    toast.info(message, { description, duration: 4000 });
  }

  public showLoadingToast(message: string): string | number {
    return toast.loading(message);
  }

  public dismissToast(toastId: string | number): void {
    toast.dismiss(toastId);
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.get<T>(url, {
      ...config,
      timeout: config?.timeout || REQUEST_TIMEOUT,
    });
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.post<T>(url, data, {
      ...config,
      timeout: config?.timeout || REQUEST_TIMEOUT,
    });
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.put<T>(url, data, {
      ...config,
      timeout: config?.timeout || REQUEST_TIMEOUT,
    });
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.patch<T>(url, data, {
      ...config,
      timeout: config?.timeout || REQUEST_TIMEOUT,
    });
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.delete<T>(url, {
      ...config,
      timeout: config?.timeout || REQUEST_TIMEOUT,
    });
    return response.data;
  }

  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

const apiService = new ApiService();
export default apiService;